// @file Composable to encapsulate content picker sources logic

import { supportsRecording, supportsVideoRecording } from '@@/bits/av_recorder'
import type { ContentPickerSourceMenuListItem } from '@@/bits/content_picker'
import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { SearchFilters } from '@@/bits/web_search'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

export const usePostContentPickerSources = (): {
  isAudioSupported: boolean
  isCameraSupported: boolean
  isScreenshareSupported: boolean
  isVideoSupported: boolean
  contentSources: ComputedRef<ContentPickerSourceMenuListItem[][]>
} => {
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceStore = useSurfaceStore()

  const isCameraSupported = !device.zoomApp
  const isAudioSupported = !device.zoomApp && supportsRecording
  const isVideoSupported = !device.zoomApp && (supportsVideoRecording || device.mobile)
  const isScreenshareSupported =
    !device.zoomApp && supportsVideoRecording && !device.mobile && (device.chrome || device.firefox)

  const contentSources = computed(() => {
    const section1: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.PadletBrowser,
        text: __('Padlet'),
        ariaLabel: __('Attach another padlet'),
        icon: 'padlet_outline',
        // There’s an issue where exporting the correct stroke width for the Padlet logo specifically breaks the svg, so the exported value is thinner than it should be, and we manually override it here
        iconStrokeWidth: 0.5,
        color: 'purple',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.PadletBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.PadletBrowser,
        testId: 'attachAnotherPadletButton',
      },
      {
        type: ContentPickerSource.FileBrowser,
        text: __('Upload'),
        ariaLabel: __('Upload a file'),
        icon: 'upload_outline',
        color: 'purple',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.FileBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.FileBrowser,
        hiddenInputId: 'content-hidden-file-input',
        testId: 'contentPickerSourceUploadButton',
      },
      {
        type: ContentPickerSource.LinkInput,
        text: __('Link'),
        ariaLabel: __('Attach a link'),
        icon: 'link_outline',
        color: 'purple',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.LinkInput),
        disabled: false,
        actionType: 'trigger-link-input',
        testId: 'contentPickerSourceLinkButton',
      },
    ]

    let section2: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.PhotoBooth,
        text: __('Camera'),
        ariaLabel: __('Take a photo'),
        icon: 'camera_outline',
        color: 'blue',
        visible:
          isCameraSupported && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.PhotoBooth),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.PhotoBooth,
        hiddenInputId: 'content-hidden-image-input',
        testId: 'contentPickerSourceCameraButton',
      },
      {
        type: ContentPickerSource.VideoRecorder,
        text: __('Video recorder'),
        ariaLabel: __('Record a video'),
        icon: 'video_outline',
        color: 'blue',
        visible:
          isVideoSupported && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.VideoRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.VideoRecorder,
        hiddenInputId:
          device.mobile && // On mobile devices, trigger a native camera instead of our own camera recorder component
          !isAppUsing('videoRecordingLimits') // New camera features requires that we use our own video recorder component
            ? 'content-hidden-video-input'
            : undefined,
        testId: 'contentPickerSourceVideoButton',
      },
      {
        type: ContentPickerSource.AudioRecorder,
        text: __('Audio recorder'),
        ariaLabel: __('Record an audio'),
        icon: 'microphone_outline',
        color: 'blue',
        visible:
          isAudioSupported && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AudioRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.AudioRecorder,
        testId: 'contentPickerSourceAudioButton',
      },
      {
        type: ContentPickerSource.ScreenshareRecorder,
        text: __('Screen recorder'),
        ariaLabel: __('Record your screen'),
        icon: 'screen_recorder_outline',
        color: 'blue',
        visible:
          !device.tablet &&
          !device.phone &&
          isScreenshareSupported &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.ScreenshareRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.ScreenshareRecorder,
        testId: 'contentPickerSourceScreenRecordButton',
      },
      {
        type: ContentPickerSource.DrawingCanvas,
        text: __('Draw'),
        ariaLabel: __('Draw something'),
        icon: 'draw_outline',
        color: 'blue',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.DrawingCanvas),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.DrawingCanvas,
        testId: 'contentPickerSourceDrawButton',
      },
    ]
    if (surfaceStore.isWhiteboard) {
      // Remove DrawingCanvas from section2 if the whiteboard is enabled
      section2 = section2.filter((item) => item.type !== ContentPickerSource.DrawingCanvas)
    }
    const section3: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.Poll,
        text: __('Poll'),
        ariaLabel: __('Make a poll'),
        icon: 'poll_outline',
        color: 'green',
        isNew: true,
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.Poll),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.Poll,
        testId: 'makeAPollButton',
      },
      {
        type: ContentPickerSource.GoogleDrive,
        text: __('Google Drive'),
        ariaLabel: __('Upload from Google Drive'),
        icon: 'google_drive_outline',
        color: 'green',
        isNew: true,
        visible:
          isAppUsing('googleDrive') &&
          surfacePermissionsStore.amIRegistered &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.GoogleDrive),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.GoogleDrive,
        testId: 'contentPickerSourceGoogleDriveButton',
      },
      {
        type: ContentPickerSource.AiImageSearch,
        text: __("I can't draw"),
        ariaLabel: __('Use AI Art Generator'),
        icon: 'sparkles_outline',
        color: 'green',
        isNew: true,
        visible:
          isAppUsing('aiImageSearch') &&
          surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AiImageSearch),
        disabled: false,
        actionType: 'trigger-ai-image-search',
        testId: 'contentPickerSourceAiImageSearchButton',
      },
      {
        type: ContentPickerSource.AiTalk,
        text: __('Talk for me'),
        ariaLabel: __('Use AI Speech Generator'),
        icon: 'communicate',
        color: 'green',
        isNew: true,
        visible: isAppUsing('aiTalk') && surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AiTalk),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.AiTalk,
        testId: 'contentPickerSourceAiTalkButton',
      },
    ]
    const section4: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.ImageSearch,
        text: __('Image search'),
        ariaLabel: __('Search for an image'),
        icon: 'image_search_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.ImageSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Images,
        testId: 'contentPickerSourceImageSearchButton',
      },
      {
        type: ContentPickerSource.GifSearch,
        text: __('GIF'),
        ariaLabel: __('Search for a GIF'),
        icon: 'gif',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.GifSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Gif,
        testId: 'contentPickerSourceGifSearchButton',
      },
      {
        type: ContentPickerSource.VideoSearch,
        text: __('YouTube'),
        ariaLabel: __('Search on YouTube'),
        icon: 'youtube_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.VideoSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Videos,
        testId: 'contentPickerSourceVideoSearchButton',
      },
      {
        type: ContentPickerSource.AudioSearch,
        text: __('Spotify'),
        ariaLabel: __('Search on Spotify'),
        icon: 'spotify_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.AudioSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Audio,
        testId: 'contentPickerSourceAudioSearchButton',
      },
      {
        type: ContentPickerSource.WebBrowser,
        text: __('Web search'),
        ariaLabel: __('Search on the web'),
        icon: 'web_search_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.WebBrowser),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Web,
        testId: 'contentPickerSourceWebSearchButton',
      },
      {
        type: ContentPickerSource.MapBrowser,
        text: __('Location'),
        ariaLabel: __('Add a location'),
        icon: 'location_outline',
        color: 'orange',
        visible: surfacePostPropertiesStore.isAttachmentTypeEnabled(ContentPickerSource.MapBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.MapBrowser,
        testId: 'contentPickerSourceLocationSearchButton',
      },
    ]
    return [section1, section2, section3, section4]
  })

  return { isAudioSupported, isCameraSupported, isScreenshareSupported, isVideoSupported, contentSources }
}
